import axios from 'axios';
import { isPrerendering } from '../utils/prerender';

// Auto-detect API URL based on current hostname
const getApiUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // During prerendering, no backend is available — use a placeholder
  if (isPrerendering()) {
    return 'http://localhost:5000/api';
  }

  // Get current hostname and port
  const hostname = window.location.hostname;
  
  // If accessing from network IP (not localhost), use the same IP for API
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    // Assume backend is on port 5000
    return `http://${hostname}:5000/api`;
  }
  
  // Default to localhost for local development
  return 'http://localhost:5000/api';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Block all API calls during react-snap prerendering
// The backend is not running, so requests would fail and return HTML error
// pages, causing "SyntaxError: Unexpected token '<'" crashes
api.interceptors.request.use(
  (config) => {
    if (isPrerendering()) {
      const error = new Error('API calls are skipped during prerendering');
      error.code = 'PRERENDER_SKIP';
      return Promise.reject(error);
    }
    return config;
  }
);

// Add token to requests
api.interceptors.request.use(
  (config) => {
    // For FormData, do NOT set Content-Type - browser must set multipart/form-data with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    try {
      const token = localStorage.getItem('token_student');
      if (typeof token === 'string' && token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // localStorage not available or corrupted
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration and network errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token_student');
      localStorage.removeItem('user_student');
      window.location.href = '/login';
    }

    if (process.env.NODE_ENV === 'development' && !error.response) {
      console.error('Network error:', {
        message: error.message,
        code: error.code,
        apiUrl: API_URL,
        currentHost: window.location.hostname,
      });
    }

    return Promise.reject(error);
  }
);

export default api;

